/**
 * Fetches a short-lived JWT identifying the current user to the Featurebase
 * feedback widget. The JWT is passed to <FeaturebaseProvider> via the
 * `featurebaseJwt` prop so the widget skips its built-in sign-in prompt and
 * attributes feedback to the right user.
 *
 * The backend signs JWTs with a 60-minute TTL (see
 * backend/users/services/featurebase.py). We refetch every 50 minutes for
 * foregrounded tabs and also on window focus, so a user who leaves the tab
 * backgrounded for over an hour gets a fresh token before the widget sends
 * a stale JWT. React Query's staleTime prevents the focus listener from
 * hammering the backend on every tab switch.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '../../services/api'
import { getAppEnv } from '../../lib/env'
import { isFeaturebaseEnabled } from '../../lib/feedback-widgets'
import { useMe } from './useMe'

const JWT_REFRESH_INTERVAL_MS = 50 * 60 * 1000
const JWT_STALE_TIME_MS = 45 * 60 * 1000

export function useFeaturebaseJwt() {
  const { data: me } = useMe()
  const hasAppId = Boolean(import.meta.env.VITE_FEATUREBASE_APP_ID)

  return useQuery({
    queryKey: ['featurebase-jwt', me?.id],
    queryFn: () => api.getFeaturebaseJwt(),
    enabled: Boolean(me) && isFeaturebaseEnabled(getAppEnv()) && hasAppId,
    staleTime: JWT_STALE_TIME_MS,
    refetchInterval: JWT_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: 1,
  })
}
